import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Plus, Scissors, Pencil, Trash2, Store, Percent, Package,
  User, Clock, Calendar, CheckCircle, XCircle, DollarSign, Wallet, CreditCard,
  Eye, EyeOff, Key, Hash, Heart, Sparkles, Palette, FlaskConical
} from 'lucide-react';
import BarberCommissionsDialog from '@/components/admin-barbers/BarberCommissionsDialog';
import BarberTipsDialog from '@/components/admin-barbers/BarberTipsDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBarbers() {
  const { getAuthHeaders } = useAuth();
  const [barbers, setBarbers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [pendingCommissions, setPendingCommissions] = useState(null);
  const [gapReport, setGapReport] = useState(null);
  const [gapLoading, setGapLoading] = useState(false);
  const [resettingRetention, setResettingRetention] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBarber, setEditingBarber] = useState(null);
  const [selectedBarberCommissions, setSelectedBarberCommissions] = useState(null);
  const [selectedBarberTips, setSelectedBarberTips] = useState(null);
  const [payingBarber, setPayingBarber] = useState(null);
  const [payingTips, setPayingTips] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    password: '',
    pin: '',
    branch_id: '',
    role_type: 'barber',
    rest_day: '',
    commission_percentage: '40',
    product_commission_percentage: '10',
    color_commission_percentage: '30',
    treatment_commission_percentage: '30',
    nails_commission_percentage: '30',
    exclude_from_ranking: false,
    avg_ticket_goal: '0',
    services_goal: '0',
    products_revenue_goal: '0',
    extra_services_goal: '0',
    neurone_revenue_goal: '0',
  });

  // Day names for rest day selector
  const DAY_NAMES = [
    { value: '0', label: 'Lunes' },
    { value: '1', label: 'Martes' },
    { value: '2', label: 'Miércoles' },
    { value: '3', label: 'Jueves' },
    { value: '4', label: 'Viernes' },
    { value: '5', label: 'Sábado' },
    { value: '6', label: 'Domingo' },
  ];

  const getRestDayName = (dayNumber) => {
    if (dayNumber === null || dayNumber === undefined || dayNumber === '') return null;
    const day = DAY_NAMES.find(d => d.value === String(dayNumber));
    return day ? day.label : null;
  };

  useEffect(() => {
    fetchData();
  }, []);

  const currentMonthRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const pad = (n) => String(n).padStart(2, '0');
    const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    return { start_date: iso(start), end_date: iso(now) };
  };

  const fetchGapReport = async () => {
    setGapLoading(true);
    try {
      const { start_date, end_date } = currentMonthRange();
      const res = await axios.get(
        `${API}/barbers/attendance-gap-report?start_date=${start_date}&end_date=${end_date}`,
        getAuthHeaders()
      );
      setGapReport(res.data);
    } catch (_e) {
      // silent
    } finally {
      setGapLoading(false);
    }
  };

  const handleResetRetention = async () => {
    const ok = window.confirm('¿Recuperar TODAS las comisiones retenidas? Esta acción es segura, no borra nada — solo vuelve visibles las comisiones que fueron ocultadas por la regla anterior.');
    if (!ok) return;
    setResettingRetention(true);
    try {
      const res = await axios.post(`${API}/barbers/commissions/reset-retention`, {}, getAuthHeaders());
      const n = res.data?.modified_count ?? 0;
      toast.success(n > 0 ? `${n} comisiones recuperadas` : 'No había comisiones retenidas');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al recuperar comisiones');
    } finally {
      setResettingRetention(false);
    }
  };

  const fetchData = async () => {
    try {
      const [barbersRes, branchesRes, attendanceRes, commissionsRes] = await Promise.all([
        axios.get(`${API}/barbers`, getAuthHeaders()),
        axios.get(`${API}/branches`, getAuthHeaders()),
        axios.get(`${API}/attendance`, getAuthHeaders()),
        axios.get(`${API}/barbers/commissions/pending`, getAuthHeaders()),
      ]);
      setBarbers(barbersRes.data);
      setBranches(branchesRes.data);
      setAttendance(attendanceRes.data);
      setPendingCommissions(commissionsRes.data);
      fetchGapReport(); // fire-and-forget
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        name: formData.name,
        branch_id: formData.branch_id,
        role_type: formData.role_type,
        pin: formData.pin || null,
        rest_day: formData.rest_day !== '' && formData.rest_day !== 'none' ? parseInt(formData.rest_day) : null,
        commission_percentage: parseFloat(formData.commission_percentage),
        product_commission_percentage: parseFloat(formData.product_commission_percentage),
        exclude_from_ranking: !!formData.exclude_from_ranking,
        avg_ticket_goal: parseFloat(formData.avg_ticket_goal) || 0,
        services_goal: parseInt(formData.services_goal) || 0,
        products_revenue_goal: parseFloat(formData.products_revenue_goal) || 0,
        extra_services_goal: parseInt(formData.extra_services_goal) || 0,
        neurone_revenue_goal: parseFloat(formData.neurone_revenue_goal) || 0,
      };
      
      // Add stylist-specific commissions if applicable
      if (formData.role_type === 'stylist') {
        payload.color_commission_percentage = parseFloat(formData.color_commission_percentage) || 30;
        payload.treatment_commission_percentage = parseFloat(formData.treatment_commission_percentage) || 30;
        payload.nails_commission_percentage = parseFloat(formData.nails_commission_percentage) || 30;
      }
      
      if (editingBarber) {
        await axios.put(`${API}/barbers/${editingBarber.id}`, payload, getAuthHeaders());
        toast.success(formData.role_type === 'stylist' ? 'Estilista actualizado' : 'Barbero actualizado');
      } else {
        await axios.post(`${API}/barbers`, {
          ...payload,
          username: formData.username,
          password: formData.password,
        }, getAuthHeaders());
        toast.success(formData.role_type === 'stylist' ? 'Estilista creado' : 'Barbero creado');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      username: '',
      password: '',
      pin: '',
      branch_id: '',
      role_type: 'barber',
      rest_day: '',
      commission_percentage: '40',
      product_commission_percentage: '10',
      color_commission_percentage: '30',
      treatment_commission_percentage: '30',
      nails_commission_percentage: '30',
      exclude_from_ranking: false,
      avg_ticket_goal: '0',
      services_goal: '0',
      products_revenue_goal: '0',
    });
    setEditingBarber(null);
    setShowPassword(false);
  };

  const togglePasswordVisibility = (barberId) => {
    setVisiblePasswords(prev => ({
      ...prev,
      [barberId]: !prev[barberId]
    }));
  };

  const handleEdit = (barber) => {
    setEditingBarber(barber);
    setFormData({
      name: barber.name,
      username: barber.username,
      password: '',
      pin: barber.pin || '',
      branch_id: barber.branch_id,
      role_type: barber.role_type || 'barber',
      rest_day: barber.rest_day !== null && barber.rest_day !== undefined ? String(barber.rest_day) : '',
      commission_percentage: barber.commission_percentage.toString(),
      product_commission_percentage: (barber.product_commission_percentage || 10).toString(),
      color_commission_percentage: (barber.color_commission_percentage || 30).toString(),
      treatment_commission_percentage: (barber.treatment_commission_percentage || 30).toString(),
      nails_commission_percentage: (barber.nails_commission_percentage || 30).toString(),
      exclude_from_ranking: !!barber.exclude_from_ranking,
      avg_ticket_goal: (barber.avg_ticket_goal || 0).toString(),
      services_goal: (barber.services_goal || 0).toString(),
      products_revenue_goal: (barber.products_revenue_goal || 0).toString(),
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este barbero?')) return;
    try {
      await axios.delete(`${API}/barbers/${id}`, getAuthHeaders());
      toast.success('Barbero eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const viewCommissions = async (barber) => {
    try {
      const response = await axios.get(`${API}/barbers/${barber.id}/commissions`, getAuthHeaders());
      setSelectedBarberCommissions(response.data);
    } catch (error) {
      toast.error('Error al cargar comisiones');
    }
  };

  const handleDeleteCommission = async (commissionId, barberId) => {
    if (!window.confirm('¿Eliminar esta comisión? Esta acción no se puede deshacer.')) return;
    try {
      await axios.delete(`${API}/barbers/commissions/${commissionId}`, getAuthHeaders());
      toast.success('Comisión eliminada');
      // Refresh commissions for the barber
      const response = await axios.get(`${API}/barbers/${barberId}/commissions`, getAuthHeaders());
      setSelectedBarberCommissions(response.data);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar comisión');
    }
  };

  const handlePayCommissions = async (barberId) => {
    if (!window.confirm('¿Marcar todas las comisiones como pagadas?')) return;
    try {
      await axios.post(`${API}/barbers/${barberId}/commissions/pay`, {}, getAuthHeaders());
      toast.success('Comisiones pagadas');
      fetchData();
      setPayingBarber(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al pagar comisiones');
    }
  };

  const handlePayServiceCommissions = async (barberId) => {
    if (!window.confirm('¿Marcar las comisiones de SERVICIOS como pagadas?')) return;
    try {
      const response = await axios.post(`${API}/barbers/${barberId}/commissions/pay/services`, {}, getAuthHeaders());
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al pagar comisiones de servicios');
    }
  };

  const handlePayProductCommissions = async (barberId) => {
    if (!window.confirm('¿Marcar las comisiones de PRODUCTOS como pagadas?')) return;
    try {
      const response = await axios.post(`${API}/barbers/${barberId}/commissions/pay/products`, {}, getAuthHeaders());
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al pagar comisiones de productos');
    }
  };

  const viewTips = async (barber) => {
    try {
      const response = await axios.get(`${API}/barbers/${barber.id}/tips`, getAuthHeaders());
      setSelectedBarberTips(response.data);
    } catch (error) {
      toast.error('Error al cargar propinas');
    }
  };

  const handlePayTips = async (barberId) => {
    if (!window.confirm('¿Marcar todas las propinas como pagadas?')) return;
    setPayingTips(true);
    try {
      const response = await axios.post(`${API}/barbers/${barberId}/tips/pay`, {}, getAuthHeaders());
      toast.success(response.data.message);
      fetchData();
      setSelectedBarberTips(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al pagar propinas');
    } finally {
      setPayingTips(false);
    }
  };

  const getBarberAttendance = (barberId) => {
    return attendance.filter(a => a.barber_id === barberId).slice(0, 7);
  };

  const getBarberPendingCommission = (barberId) => {
    if (!pendingCommissions) return 0;
    const barberData = pendingCommissions.by_barber?.find(b => b.barber_id === barberId);
    return barberData?.pending_commission || 0;
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-barbers">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl text-white tracking-tight">Barberos y Estilistas</h1>
            <p className="text-zinc-500 mt-1">Gestiona el equipo de barberos y estilistas</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button 
                className="bg-gold text-black hover:bg-gold-dark"
                data-testid="add-barber-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuevo
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl text-white">
                  {editingBarber 
                    ? (formData.role_type === 'stylist' ? 'Editar Estilista' : 'Editar Barbero')
                    : (formData.role_type === 'stylist' ? 'Nuevo Estilista' : 'Nuevo Barbero')
                  }
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                {/* Role Type Selector - First field */}
                <div className="space-y-2">
                  <Label className="text-zinc-400">Tipo de Empleado</Label>
                  <Select
                    value={formData.role_type}
                    onValueChange={(value) => setFormData({ ...formData, role_type: value })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="role-type-select">
                      <SelectValue placeholder="Seleccionar tipo" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="barber" className="text-white">
                        <div className="flex items-center gap-2">
                          <Scissors className="w-4 h-4" />
                          Barbero
                        </div>
                      </SelectItem>
                      <SelectItem value="stylist" className="text-white">
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-4 h-4" />
                          Estilista
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-400">Nombre Completo</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder={formData.role_type === 'stylist' ? 'María López' : 'Carlos Hernández'}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="barber-name-input"
                    required
                  />
                </div>
                {!editingBarber && (
                  <>
                    <div className="space-y-2">
                      <Label className="text-zinc-400">Usuario (para login)</Label>
                      <Input
                        value={formData.username}
                        onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase().replace(/\s/g, '') })}
                        placeholder={formData.role_type === 'stylist' ? 'maria' : 'carlos'}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        data-testid="barber-username-input"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-400">Contraseña</Label>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          placeholder="••••••••"
                          className="bg-zinc-800 border-zinc-700 text-white pr-10"
                          data-testid="barber-password-input"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white transition-colors"
                          data-testid="toggle-barber-password-visibility"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </>
                )}
                <div className="space-y-2">
                  <Label className="text-zinc-400">PIN de Asistencia (4 dígitos)</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={4}
                    value={formData.pin}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                      setFormData({ ...formData, pin: val });
                    }}
                    placeholder="Ej: 1234"
                    className="bg-zinc-800 border-zinc-700 text-white tracking-widest text-center text-lg"
                    data-testid="barber-pin-input"
                  />
                  <p className="text-xs text-zinc-500">PIN para check-in / check-out del barbero</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">Sucursal</Label>
                  <Select
                    value={formData.branch_id}
                    onValueChange={(value) => setFormData({ ...formData, branch_id: value })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                      <SelectValue placeholder="Seleccionar sucursal" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {branches.map((branch) => (
                        <SelectItem key={branch.id} value={branch.id} className="text-white">
                          {branch.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Rest Day Selector */}
                <div className="space-y-2">
                  <Label className="text-zinc-400 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Día de Descanso
                  </Label>
                  <Select
                    value={formData.rest_day}
                    onValueChange={(value) => setFormData({ ...formData, rest_day: value })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="rest-day-select">
                      <SelectValue placeholder="Sin día de descanso" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="none" className="text-zinc-400">Sin día de descanso</SelectItem>
                      {DAY_NAMES.map((day) => (
                        <SelectItem key={day.value} value={day.value} className="text-white">
                          {day.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-600">Este día no aparecerá disponible en la agenda ni se contará para asistencia</p>
                </div>

                {/* Base Commissions - Always visible */}
                <div className="pt-2 border-t border-zinc-800">
                  <p className="text-sm text-zinc-500 mb-3">Comisiones Base</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-400 text-sm">Comisión Servicio (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={formData.commission_percentage}
                        onChange={(e) => setFormData({ ...formData, commission_percentage: e.target.value })}
                        placeholder="40"
                        className="bg-zinc-800 border-zinc-700 text-white"
                        data-testid="barber-commission-input"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-400 text-sm">Comisión Producto (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={formData.product_commission_percentage}
                        onChange={(e) => setFormData({ ...formData, product_commission_percentage: e.target.value })}
                        placeholder="10"
                        className="bg-zinc-800 border-zinc-700 text-white"
                        data-testid="barber-product-commission-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Exclude from ranking */}
                <div className="pt-2 border-t border-zinc-800">
                  <label className="flex items-start gap-3 cursor-pointer" data-testid="exclude-ranking-toggle">
                    <input
                      type="checkbox"
                      checked={!!formData.exclude_from_ranking}
                      onChange={(e) => setFormData({ ...formData, exclude_from_ranking: e.target.checked })}
                      className="mt-0.5 w-4 h-4 rounded accent-gold cursor-pointer"
                      data-testid="exclude-ranking-checkbox"
                    />
                    <div className="flex-1">
                      <p className="text-sm text-zinc-200">Excluir del ranking por zona</p>
                      <p className="text-xs text-zinc-500 mt-0.5">
                        Sus ventas no aparecerán en el ranking de colaboradores ni en el podio Top 3.
                        Útil para dueños o socios que también cortan pero no compiten en métricas.
                      </p>
                    </div>
                  </label>
                </div>

                {/* Personal Monthly Goals */}
                <div className="pt-3 border-t border-zinc-800" data-testid="personal-goals-section">
                  <p className="text-sm text-zinc-300 mb-1 font-medium">Metas Personales del Mes</p>
                  <p className="text-xs text-zinc-500 mb-3">Independientes de la meta de sucursal. Se mostrarán en el panel del barbero.</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-zinc-400 text-xs">Ticket Promedio ($)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="10"
                        value={formData.avg_ticket_goal}
                        onChange={(e) => setFormData({ ...formData, avg_ticket_goal: e.target.value })}
                        placeholder="0"
                        className="bg-zinc-800 border-zinc-700 text-white"
                        data-testid="goal-avg-ticket-input"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-zinc-400 text-xs">Servicios (#)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        value={formData.services_goal}
                        onChange={(e) => setFormData({ ...formData, services_goal: e.target.value })}
                        placeholder="0"
                        className="bg-zinc-800 border-zinc-700 text-white"
                        data-testid="goal-services-input"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-zinc-400 text-xs">Productos ($)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="50"
                        value={formData.products_revenue_goal}
                        onChange={(e) => setFormData({ ...formData, products_revenue_goal: e.target.value })}
                        placeholder="0"
                        className="bg-zinc-800 border-zinc-700 text-white"
                        data-testid="goal-products-input"
                      />
                    </div>
                  </div>
                  {/* EXTRA + Neurone goals */}
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div className="space-y-1">
                      <Label className="text-orange-400 text-xs flex items-center gap-1">
                        Servicios EXTRA (#)
                        <span className="text-[8px] px-1 py-0.5 rounded bg-orange-500/20 text-orange-400 uppercase">Extra</span>
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        value={formData.extra_services_goal}
                        onChange={(e) => setFormData({ ...formData, extra_services_goal: e.target.value })}
                        placeholder="0"
                        className="bg-zinc-800 border-zinc-700 text-white"
                        data-testid="goal-extra-services-input"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-purple-400 text-xs flex items-center gap-1">
                        Productos NEURONE ($)
                        <span className="text-[8px] px-1 py-0.5 rounded bg-purple-500/20 text-purple-400 uppercase">Neurone</span>
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        step="50"
                        value={formData.neurone_revenue_goal}
                        onChange={(e) => setFormData({ ...formData, neurone_revenue_goal: e.target.value })}
                        placeholder="0"
                        className="bg-zinc-800 border-zinc-700 text-white"
                        data-testid="goal-neurone-revenue-input"
                      />
                    </div>
                  </div>
                  <p className="text-[10px] text-zinc-600 mt-2">Deja en 0 cualquier meta que no aplique. Las metas EXTRA y NEURONE se calculan usando los flags de servicios/productos.</p>
                </div>

                {/* Stylist-specific Commissions */}
                {formData.role_type === 'stylist' && (
                  <div className="pt-2 border-t border-zinc-800">
                    <p className="text-sm text-zinc-500 mb-3 flex items-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      Comisiones Estilista
                    </p>
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label className="text-zinc-400 text-sm flex items-center gap-2">
                          <Palette className="w-4 h-4 text-purple-400" />
                          Comisión Servicio Color (%)
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={formData.color_commission_percentage}
                          onChange={(e) => setFormData({ ...formData, color_commission_percentage: e.target.value })}
                          placeholder="30"
                          className="bg-zinc-800 border-zinc-700 text-white"
                          data-testid="stylist-color-commission-input"
                        />
                        <p className="text-xs text-zinc-600">Para servicios que contengan &quot;Color&quot; en el nombre</p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-400 text-sm flex items-center gap-2">
                          <FlaskConical className="w-4 h-4 text-blue-400" />
                          Comisión Tratamiento (%)
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={formData.treatment_commission_percentage}
                          onChange={(e) => setFormData({ ...formData, treatment_commission_percentage: e.target.value })}
                          placeholder="30"
                          className="bg-zinc-800 border-zinc-700 text-white"
                          data-testid="stylist-treatment-commission-input"
                        />
                        <p className="text-xs text-zinc-600">Para servicios que contengan &quot;Tratamiento&quot; en el nombre</p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-400 text-sm flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-pink-400" />
                          Comisión Servicio Uñas (%)
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={formData.nails_commission_percentage}
                          onChange={(e) => setFormData({ ...formData, nails_commission_percentage: e.target.value })}
                          placeholder="30"
                          className="bg-zinc-800 border-zinc-700 text-white"
                          data-testid="stylist-nails-commission-input"
                        />
                        <p className="text-xs text-zinc-600">Para servicios que contengan &quot;Uñas&quot; en el nombre</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                    className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-gold text-black hover:bg-gold-dark"
                    data-testid="barber-submit-btn"
                  >
                    {editingBarber ? 'Actualizar' : 'Crear'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Pending Commissions Summary */}
        {pendingCommissions && pendingCommissions.total_pending > 0 && (
          <Card className="bg-gold/10 border-gold/30 p-6">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gold/20 flex items-center justify-center">
                  <Wallet className="w-7 h-7 text-gold" />
                </div>
                <div>
                  <p className="text-xs text-gold uppercase tracking-widest">Total Comisiones por Pagar</p>
                  <p className="font-heading text-4xl text-gold">${pendingCommissions.total_pending.toFixed(2)}</p>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <p className="text-sm text-zinc-400">
                  {pendingCommissions.by_barber?.filter(b => b.pending_commission > 0).length || 0} barberos con comisiones pendientes
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleResetRetention}
                  disabled={resettingRetention}
                  className="border-gold/40 text-gold hover:bg-gold/10 hover:text-gold h-8"
                  title="Vuelve visibles las comisiones que quedaron marcadas como retenidas (regla anterior)."
                  data-testid="reset-retention-btn"
                >
                  {resettingRetention ? 'Recuperando...' : 'Recuperar comisiones retenidas'}
                </Button>
              </div>
            </div>
          </Card>
        )}

        <Tabs defaultValue="barbers" className="w-full">
          <TabsList className="bg-zinc-900 border border-zinc-800">
            <TabsTrigger value="barbers" className="data-[state=active]:bg-gold data-[state=active]:text-black">
              <Scissors className="w-4 h-4 mr-2" />
              Barberos
            </TabsTrigger>
            <TabsTrigger value="commissions" className="data-[state=active]:bg-gold data-[state=active]:text-black">
              <DollarSign className="w-4 h-4 mr-2" />
              Comisiones
            </TabsTrigger>
            <TabsTrigger value="attendance" className="data-[state=active]:bg-gold data-[state=active]:text-black">
              <Clock className="w-4 h-4 mr-2" />
              Asistencias
            </TabsTrigger>
          </TabsList>

          <TabsContent value="barbers" className="mt-6">
            {loading ? (
              <div className="text-center py-12 text-zinc-500">Cargando...</div>
            ) : barbers.length === 0 ? (
              <Card className="bg-zinc-900/50 border-zinc-800 p-12 text-center">
                <Scissors className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500">No hay barberos registrados</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {barbers.map((barber) => (
                  <Card 
                    key={barber.id} 
                    className="bg-zinc-900/50 border-zinc-800 p-5 hover:border-zinc-700 transition-colors"
                    data-testid={`barber-card-${barber.id}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center">
                        <User className="w-6 h-6 text-gold" />
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => viewCommissions(barber)}
                          className="w-7 h-7 rounded-sm bg-zinc-800 hover:bg-green-500/20 flex items-center justify-center text-zinc-400 hover:text-green-500 transition-colors"
                          title="Ver comisiones"
                        >
                          <DollarSign className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => viewTips(barber)}
                          className="w-7 h-7 rounded-sm bg-zinc-800 hover:bg-pink-500/20 flex items-center justify-center text-zinc-400 hover:text-pink-500 transition-colors"
                          title="Ver propinas"
                        >
                          <Heart className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => handleEdit(barber)}
                          className="w-7 h-7 rounded-sm bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => handleDelete(barber.id)}
                          className="w-7 h-7 rounded-sm bg-zinc-800 hover:bg-red-500/20 flex items-center justify-center text-zinc-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    <h3 className="font-heading text-lg text-white mb-1">{barber.name}</h3>
                    <p className="text-sm text-zinc-500 mb-3">@{barber.username}</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1 text-zinc-500">
                          <Store className="w-3 h-3" />
                          Sucursal
                        </span>
                        <span className="text-zinc-300">{barber.branch_name}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1 text-zinc-500">
                          <Calendar className="w-3 h-3" />
                          Descanso
                        </span>
                        <span className={`${barber.rest_day !== null && barber.rest_day !== undefined ? 'text-orange-400' : 'text-zinc-600'}`}>
                          {getRestDayName(barber.rest_day) || 'Sin descanso'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1 text-zinc-500">
                          <Scissors className="w-3 h-3" />
                          Com. Servicios
                        </span>
                        <span className="text-gold font-semibold">{barber.commission_percentage}%</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1 text-zinc-500">
                          <Package className="w-3 h-3" />
                          Com. Productos
                        </span>
                        <span className="text-blue-400 font-semibold">{barber.product_commission_percentage || 10}%</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1 text-zinc-500">
                          <Wallet className="w-3 h-3" />
                          Por Pagar
                        </span>
                        <span className="text-green-500 font-semibold">
                          ${getBarberPendingCommission(barber.id).toFixed(2)}
                        </span>
                      </div>
                    </div>
                    
                    {/* Credentials */}
                    <div className="mt-4 pt-4 border-t border-zinc-800">
                      <p className="text-xs text-zinc-500 uppercase tracking-widest mb-2">Credenciales de Acceso</p>
                      <div className="space-y-2 p-3 bg-zinc-800/50 rounded-sm">
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-1 text-xs text-zinc-500">
                            <User className="w-3 h-3" />
                            Usuario
                          </span>
                          <span className="text-sm text-zinc-300 font-mono">{barber.username}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-1 text-xs text-zinc-500">
                            <Key className="w-3 h-3" />
                            Contraseña
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-zinc-300 font-mono">
                              {visiblePasswords[barber.id] 
                                ? (barber.plain_password || '••••••••') 
                                : '••••••••'}
                            </span>
                            {barber.plain_password && (
                              <button
                                onClick={() => togglePasswordVisibility(barber.id)}
                                className="text-zinc-500 hover:text-gold transition-colors"
                              >
                                {visiblePasswords[barber.id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-1 text-xs text-zinc-500">
                            <Hash className="w-3 h-3" />
                            PIN
                          </span>
                          <span className="text-sm text-zinc-300 font-mono tracking-widest" data-testid={`barber-pin-display-${barber.id}`}>
                            {barber.pin || <span className="text-zinc-600 text-xs">Sin PIN</span>}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Recent Attendance */}
                    <div className="mt-4 pt-4 border-t border-zinc-800">
                      <p className="text-xs text-zinc-500 uppercase tracking-widest mb-2">Últimas asistencias</p>
                      <div className="flex gap-1">
                        {getBarberAttendance(barber.id).length > 0 ? (
                          getBarberAttendance(barber.id).map((a) => (
                            <div
                              key={a.date || a.check_in}
                              className={`w-6 h-6 rounded-sm flex items-center justify-center ${
                                a.check_out ? 'bg-green-500/20' : 'bg-yellow-500/20'
                              }`}
                              title={`${a.date}: ${formatTime(a.check_in)} - ${formatTime(a.check_out)}`}
                            >
                              {a.check_out ? (
                                <CheckCircle className="w-3 h-3 text-green-500" />
                              ) : (
                                <Clock className="w-3 h-3 text-yellow-500" />
                              )}
                            </div>
                          ))
                        ) : (
                          <p className="text-xs text-zinc-600">Sin registros</p>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="commissions" className="mt-6">
            <Tabs defaultValue="all" className="w-full">
              <TabsList className="bg-zinc-800 border border-zinc-700 mb-4">
                <TabsTrigger value="all" className="data-[state=active]:bg-gold data-[state=active]:text-black">
                  Todas
                </TabsTrigger>
                <TabsTrigger value="services" className="data-[state=active]:bg-gold data-[state=active]:text-black">
                  <Scissors className="w-4 h-4 mr-1" />
                  Servicios
                </TabsTrigger>
                <TabsTrigger value="products" className="data-[state=active]:bg-blue-500 data-[state=active]:text-white">
                  <Package className="w-4 h-4 mr-1" />
                  Productos
                </TabsTrigger>
              </TabsList>

              {/* All Commissions Tab */}
              <TabsContent value="all">
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
                    <h3 className="text-sm text-zinc-500 uppercase tracking-widest">Todas las Comisiones por Barbero</h3>
                    {pendingCommissions && (
                      <span className="text-gold font-semibold">
                        Total: ${pendingCommissions.total_pending?.toFixed(2) || '0.00'}
                      </span>
                    )}
                  </div>
                  <ScrollArea className="h-[450px]">
                    {!pendingCommissions || pendingCommissions.by_barber?.length === 0 ? (
                      <div className="p-12 text-center">
                        <DollarSign className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                        <p className="text-zinc-500">No hay comisiones pendientes</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-zinc-800">
                        {pendingCommissions.by_barber?.map((barber) => (
                          <div key={barber.barber_id} className="p-4 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
                                <User className="w-5 h-5 text-gold" />
                              </div>
                              <div>
                                <p className="text-white font-medium">{barber.barber_name}</p>
                                <p className="text-sm text-zinc-500">
                                  {barber.branch_name} • {barber.services_count} servicios, {barber.products_count || 0} productos
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className="text-xs text-zinc-500 uppercase">Por Pagar</p>
                                <p className="text-2xl font-heading text-green-500">
                                  ${barber.pending_commission.toFixed(2)}
                                </p>
                              </div>
                              {barber.pending_commission > 0 && (
                                <Button
                                  size="sm"
                                  onClick={() => handlePayCommissions(barber.barber_id)}
                                  className="bg-green-500/20 text-green-500 hover:bg-green-500/30"
                                >
                                  <CreditCard className="w-4 h-4 mr-1" />
                                  Pagar Todo
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </Card>
              </TabsContent>

              {/* Services Commissions Tab */}
              <TabsContent value="services">
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
                    <h3 className="text-sm text-zinc-500 uppercase tracking-widest flex items-center gap-2">
                      <Scissors className="w-4 h-4 text-gold" />
                      Comisiones por Servicios
                    </h3>
                    {pendingCommissions && (
                      <span className="text-gold font-semibold">
                        Total: ${pendingCommissions.by_barber?.reduce((sum, b) => sum + (b.services_commission || 0), 0).toFixed(2) || '0.00'}
                      </span>
                    )}
                  </div>
                  <ScrollArea className="h-[450px]">
                    {!pendingCommissions || pendingCommissions.by_barber?.filter(b => (b.services_commission || 0) > 0).length === 0 ? (
                      <div className="p-12 text-center">
                        <Scissors className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                        <p className="text-zinc-500">No hay comisiones de servicios pendientes</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-zinc-800">
                        {pendingCommissions.by_barber?.filter(b => (b.services_commission || 0) > 0).map((barber) => (
                          <div key={barber.barber_id} className="p-4 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
                                <Scissors className="w-5 h-5 text-gold" />
                              </div>
                              <div>
                                <p className="text-white font-medium">{barber.barber_name}</p>
                                <p className="text-sm text-zinc-500">
                                  {barber.branch_name} • {barber.services_count} servicios
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className="text-xs text-gold uppercase">Servicios</p>
                                <p className="text-2xl font-heading text-gold">
                                  ${(barber.services_commission || 0).toFixed(2)}
                                </p>
                              </div>
                              {(barber.services_commission || 0) > 0 && (
                                <Button
                                  size="sm"
                                  onClick={() => handlePayServiceCommissions(barber.barber_id)}
                                  className="bg-gold/20 text-gold hover:bg-gold/30"
                                >
                                  <CreditCard className="w-4 h-4 mr-1" />
                                  Pagar
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </Card>
              </TabsContent>

              {/* Products Commissions Tab */}
              <TabsContent value="products">
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
                    <h3 className="text-sm text-zinc-500 uppercase tracking-widest flex items-center gap-2">
                      <Package className="w-4 h-4 text-blue-400" />
                      Comisiones por Productos
                    </h3>
                    {pendingCommissions && (
                      <span className="text-blue-400 font-semibold">
                        Total: ${pendingCommissions.by_barber?.reduce((sum, b) => sum + (b.products_commission || 0), 0).toFixed(2) || '0.00'}
                      </span>
                    )}
                  </div>
                  <ScrollArea className="h-[450px]">
                    {!pendingCommissions || pendingCommissions.by_barber?.filter(b => (b.products_commission || 0) > 0).length === 0 ? (
                      <div className="p-12 text-center">
                        <Package className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                        <p className="text-zinc-500">No hay comisiones de productos pendientes</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-zinc-800">
                        {pendingCommissions.by_barber?.filter(b => (b.products_commission || 0) > 0).map((barber) => (
                          <div key={barber.barber_id} className="p-4 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                                <Package className="w-5 h-5 text-blue-400" />
                              </div>
                              <div>
                                <p className="text-white font-medium">{barber.barber_name}</p>
                                <p className="text-sm text-zinc-500">
                                  {barber.branch_name} • {barber.products_count || 0} productos
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className="text-xs text-blue-400 uppercase">Productos</p>
                                <p className="text-2xl font-heading text-blue-400">
                                  ${(barber.products_commission || 0).toFixed(2)}
                                </p>
                              </div>
                              {(barber.products_commission || 0) > 0 && (
                                <Button
                                  size="sm"
                                  onClick={() => handlePayProductCommissions(barber.barber_id)}
                                  className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                                >
                                  <CreditCard className="w-4 h-4 mr-1" />
                                  Pagar
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </Card>
              </TabsContent>
            </Tabs>
          </TabsContent>

          <TabsContent value="attendance" className="mt-6 space-y-4">
            {/* Ventas sin check-in (mes actual) */}
            <Card className="bg-zinc-900/50 border-zinc-800" data-testid="attendance-gap-card">
              <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
                <div>
                  <h3 className="text-sm text-zinc-500 uppercase tracking-widest">Ventas sin check-in (mes actual)</h3>
                  <p className="text-xs text-zinc-600 mt-0.5">Días donde el barbero hizo ventas pero NO registró asistencia. Útil para llamadas de atención.</p>
                </div>
                <button
                  onClick={fetchGapReport}
                  className="text-xs text-gold hover:text-gold/80 underline underline-offset-2"
                  data-testid="attendance-gap-refresh"
                >
                  Recalcular
                </button>
              </div>
              {gapLoading ? (
                <div className="p-6 text-center text-zinc-500 text-sm">Calculando...</div>
              ) : !gapReport || gapReport.by_barber?.every(b => b.gap_days_count === 0) ? (
                <div className="p-6 text-center text-emerald-500 text-sm">
                  ✓ Todos los barberos están registrando su asistencia este mes.
                </div>
              ) : (
                <div className="divide-y divide-zinc-800">
                  {gapReport.by_barber.filter(b => b.gap_days_count > 0).map((b) => (
                    <div key={b.barber_id} className="p-4" data-testid={`attendance-gap-row-${b.barber_id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="text-white font-medium">{b.barber_name}</p>
                          <p className="text-xs text-zinc-500">{b.branch_name}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-red-400 font-heading text-xl">{b.gap_days_count}</p>
                          <p className="text-[10px] text-zinc-500 uppercase tracking-wider">días sin marcar</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {b.gap_days.map((d) => (
                          <span
                            key={d.date}
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-sm bg-red-500/10 border border-red-500/30 text-red-300"
                            title={`${d.sales_count} ventas · $${(d.commission_total || 0).toFixed(0)} en comisión`}
                          >
                            <Calendar className="w-3 h-3" />
                            {d.date}
                            <span className="text-zinc-500">·</span>
                            <span className="text-zinc-300">{d.sales_count}v</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Registro histórico */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <div className="p-4 border-b border-zinc-800">
                <h3 className="text-sm text-zinc-500 uppercase tracking-widest">Registro de Asistencias</h3>
              </div>
              <ScrollArea className="h-[500px]">
                {attendance.length === 0 ? (
                  <div className="p-12 text-center">
                    <Clock className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                    <p className="text-zinc-500">No hay registros de asistencia</p>
                  </div>
                ) : (
                  <div className="divide-y divide-zinc-800">
                    {attendance.map((record) => (
                      <div key={record.id} className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
                            <User className="w-5 h-5 text-gold" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{record.barber_name}</p>
                            <p className="text-sm text-zinc-500">{record.branch_name}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-zinc-400 flex items-center gap-2">
                            <Calendar className="w-3 h-3" />
                            {record.date}
                          </p>
                          <p className="text-sm">
                            <span className="text-green-500">{formatTime(record.check_in)}</span>
                            <span className="text-zinc-600 mx-2">→</span>
                            <span className={record.check_out ? 'text-red-500' : 'text-yellow-500'}>
                              {record.check_out ? formatTime(record.check_out) : 'Activo'}
                            </span>
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Commissions Dialog */}
        <BarberCommissionsDialog
          data={selectedBarberCommissions}
          onClose={() => setSelectedBarberCommissions(null)}
          onDeleteCommission={handleDeleteCommission}
        />

        {/* Tips Dialog */}
        <BarberTipsDialog
          data={selectedBarberTips}
          onClose={() => setSelectedBarberTips(null)}
          onPayTips={handlePayTips}
          paying={payingTips}
        />
      </div>
    </AdminLayout>
  );
}
